import React, {useEffect, useState} from 'react';
import {RiseInterstitialProps} from 'screens/InsomniaSurvey/components/RiseInterstitialTemplate/RiseInterstitialBase';
import styles from './TimeSelectorInterstitial.module.css';
import RiseInterstitialTemplate from 'screens/InsomniaSurvey/components/RiseInterstitialTemplate/RiseInterstitialTemplate';
import {
  DateWithTimeIntervals,
  TimeInterval,
  TimeSelector,
} from 'screens/InsomniaSurvey/components/TimeSelector/TimeSelector';
import {getValueFromSurveyData} from 'utils/getValueFromSurveyData';
import {getAppointmentAvailability} from 'externalAPIs/insurance-eligibility';
import {getPayerIdByValue} from 'surveys/insomnia_funnel/paywall/utils';
import {scheduleToDateWithTimeIntervals} from './utils';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import Analytics from 'utils/Analytics';
import LoadingState from './LoadingState';

/**
 * Survey value written by this page, read by `insomnia_booking` to decide
 * whether the care-team hand-off page is visible. Set before the user can
 * advance, so SurveyJS has the right page list when it works out which page is
 * last.
 */
export const BOOKING_AVAILABILITY = {
  available: 'available',
  none: 'none',
} as const;

const TimeSelectorInterstitial: React.FC<RiseInterstitialProps> = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [dateWithTimeIntervals, setDateWithTimeIntervals] = useState<DateWithTimeIntervals[]>([]);
  const [selectedTime, setSelectedTime] = useState<TimeInterval | undefined>(undefined);
  const [hasAvailability, setHasAvailability] = useState(true);

  const updateTime = (time: TimeInterval) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    const stateCode = getValueFromSurveyData('insurance_state');
    const insuranceCoverage = getValueFromSurveyData('insurance_coverage');
    // Providers are tagged in Healthie by payer id ("payer:blue_cross_blue_shield"),
    // not by the picker's value — "anthem", "florida_blue" and "healthy_blue" all
    // resolve to blue_cross_blue_shield. Sending the raw value returns no providers.
    const payerName = getPayerIdByValue(insuranceCoverage) ?? insuranceCoverage;

    const recordNoAvailability = (reason: 'empty' | 'error', errorMessage?: string) => {
      setHasAvailability(false);
      // Written here rather than on continue so the care-team page is already
      // visible by the time the user reaches the end of the survey.
      surveyInstance.data = {
        ...surveyInstance.data,
        booking_availability: BOOKING_AVAILABILITY.none,
        booking_availability_reason: reason,
      };
      Analytics.track('Clinic Booking No Availability', {
        reason,
        error_message: errorMessage,
        insurance_state: stateCode,
        insurance_coverage: insuranceCoverage,
        payer_id: payerName,
      });
    };

    getAppointmentAvailability(stateCode, payerName)
      .then(schedule => {
        const intervals = scheduleToDateWithTimeIntervals(schedule);
        if (intervals.length === 0) {
          recordNoAvailability('empty', schedule?.error);
          return;
        }
        setDateWithTimeIntervals(intervals);
        setHasAvailability(true);
        surveyInstance.data = {
          ...surveyInstance.data,
          booking_availability: BOOKING_AVAILABILITY.available,
        };
      })
      .catch(error => {
        console.error('Error getting appointment availability:', error);
        recordNoAvailability('error', error?.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleContinue = () => {
    if (!selectedTime) return;

    // Save the selected time to the survey
    surveyInstance.data = {
      ...surveyInstance.data,
      appointment_start_time: selectedTime.startTime.toISOString(),
      appointment_end_time: selectedTime.endTime.toISOString(),
      appointment_provider_id: selectedTime.providerId,
    };
  };

  if (isLoading) {
    return <LoadingState />;
  }

  // No bookable slot. Rather than dead-end the patient, take their contact
  // details on the next page and hand them to the care team, who schedule by
  // hand. Framed as a next step, not as a failure.
  if (!hasAvailability) {
    return (
      <RiseInterstitialTemplate
        headerText={
          <>
            <div className={styles.headerText}>Let's find a time with our care team</div>
            <div className={styles.bodyText}>
              Sessions with our sleep experts are in high demand, so there are no times to book online right now. Share
              your contact details on the next screen and our care team will reach out to schedule you directly —
              usually within one business day.
            </div>
          </>
        }
        continueButtonTitle="Continue"
      />
    );
  }

  return (
    <RiseInterstitialTemplate
      headerText={
        <>
          <div className={styles.headerText}>Your personalized plan starts with an expert session</div>
          <div className={styles.bodyText}>
            Pick a session time to get clarity on your insomnia risk and tailor your plan. You can always cancel or
            reschedule later.
          </div>
        </>
      }
      bodyText={
        <div className={styles.timeSelectorWrapper}>
          <TimeSelector
            dateWithTimeIntervals={dateWithTimeIntervals}
            selected={selectedTime}
            onSelected={time => {
              updateTime(time);
            }}
          />
        </div>
      }
      continueButtonTitle="Next"
      disabled={!selectedTime}
      onClick={handleContinue}
    />
  );
};

export default TimeSelectorInterstitial;
